import { cn } from '@/utils/styles';
import {
  formatPackPrice,
  getDiscountedPrice,
} from '@screens/ShopPage/utils/pricing';
import type { AllTestKitInfo } from '@viome/viome-base/src/usecase/Shop/ShopQuery';

export type TestPurchaseOption = 'onetime' | 'subscribe';

type TestPurchaseOptionSelectorProps = {
  test: AllTestKitInfo;
  selectedOption: TestPurchaseOption;
  onSelect: (option: TestPurchaseOption) => void;
  className?: string;
};

export const TestPurchaseOptionSelector = ({
  test,
  selectedOption,
  onSelect,
  className,
}: TestPurchaseOptionSelectorProps) => {
  const basePrice = test?.current_price ?? 0;

  const oneTime = getDiscountedPrice(
    basePrice,
    test?.discountAmount ?? 0,
    test?.discountPercentage ?? 0,
  );
  const subscribe = getDiscountedPrice(
    basePrice,
    test?.subscription?.discountAmount ?? 0,
    test?.subscription?.discountPercentage ?? 0,
  );

  const options: Array<{
    key: TestPurchaseOption;
    title: string;
    frequency?: string;
    badge?: string;
    pricing: ReturnType<typeof getDiscountedPrice>;
  }> = [
    {
      key: 'onetime',
      title: test?.purchaseTitle || 'One-time',
      frequency: test?.frequency,
      pricing: oneTime,
    },
    {
      key: 'subscribe',
      title: test?.subscription?.purchaseTitle || 'Subscribe',
      frequency: test?.subscription?.frequency,
      badge: test?.subscription?.badge,
      pricing: subscribe,
    },
  ];

  return (
    <div
      className={cn(
        'flex flex-col gap-3 md:flex-row md:items-stretch',
        className,
      )}
      role="radiogroup"
      aria-label="Purchase options"
    >
      {options.map((option) => {
        const isSelected = selectedOption === option.key;

        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(option.key)}
            className={cn(
              'relative flex flex-1 items-center justify-between gap-3 rounded-xl px-4 py-3.5 text-left transition-colors',
              isSelected
                ? 'border-2 border-[#146464] bg-white'
                : 'border-2 border-transparent bg-[#F3F5F8]',
            )}
            data-testid={`shopTests-purchaseOption-${option.key}`}
          >
            {option.badge ? (
              <span className="absolute -top-2.5 left-3 rounded-full bg-[#146464] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                {option.badge}
              </span>
            ) : null}

            <span
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                isSelected ? 'border-[#146464]' : 'border-[#B9BDC9]',
              )}
              aria-hidden="true"
            >
              {isSelected ? (
                <span className="h-2 w-2 rounded-full bg-[#146464]" />
              ) : null}
            </span>

            <div className="flex flex-col min-w-0 flex-1">
              <span className="typography-body font-medium text-primary">
                {option.title}
              </span>
              {option.frequency ? (
                <span className="text-[11px] font-medium uppercase tracking-wide text-tertiary">
                  {option.frequency}
                </span>
              ) : null}
            </div>

            <div className="flex shrink-0 items-baseline gap-1.5">
              {option.pricing.hasDiscount ? (
                <span className="typography-body text-tertiary line-through">
                  {formatPackPrice(option.pricing.originalPrice)}
                </span>
              ) : null}
              <span className="typography-h3 text-[#146464]">
                {formatPackPrice(option.pricing.discountedPrice)}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
